import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CalendarDays, Check, Loader2, MapPin, Zap, Tent, Users, Boxes, Map as MapIcon, Compass, Tag, X } from "lucide-react";

const PitchMap3D = React.lazy(() => import("@/components/PitchMap3D"));
import { PARK } from "@/lib/site";
import Footer from "@/components/Footer";
import PitchLightbox from "@/components/PitchLightbox";
import Booking from "@/components/Booking";
import { LuxeOverline } from "@/components/LuxeOverline";
import { MAP_PITCH_GROUPS, MAP_LODGES, MAP_COLORS } from "@/components/SiteMap";
import { DEFAULT_PITCH_POSITIONS, LAKE_ZONES } from "@/lib/pitchMapDefaults";
import { AERIAL_PITCH_POSITIONS, AERIAL_LAKE_ZONES } from "@/lib/aerialPositions";
import { FEATURE_ICONS, FEATURE_CHIP_COLORS, lodgeShortName } from "@/lib/parkGeometry";
import { useContent } from "@/lib/content";
import Seo from "@/components/Seo";
import lakeGuideDaisy from "@/assets/lake-guide-daisy.jpg";
import lakeGuideWalnut from "@/assets/lake-guide-walnut.jpg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const gbp = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);

/** Guest-facing pitch number — read from the unit name ("… · Pitch 3") so Walnut
    hardstandings show 1–5 (matching the site plan) even though their ids are 11–15. */
const pitchNumber = (u) => {
  const m = /Pitch (\d+)/.exec(u?.name || "");
  return m ? m[1] : (u?.id || "").split("-").pop().replace(/^0/, "");
};

/** Short unambiguous label: "Daisy 3", "Walnut 2", "Grass 7", "Tent 15". */
const shortLabel = (p) => {
  const n = pitchNumber(p);
  if (p.id.startsWith("daisy-hs")) return `Daisy ${n}`;
  if (p.id.startsWith("walnut-hs")) return `Walnut ${n}`;
  if (p.sub_type === "grass") return `Grass ${n}`;
  if (p.sub_type === "tent") return `Tent ${n}`;
  return `Pitch ${n}`;
};

/**
 * Pick Your Pitch — the interactive book-by-map page.
 * Uses the real aerial photo with draggable-position pins once Justina has
 * uploaded it (Admin → Pitch Media → "Pitch map photo & pins"); falls back to
 * the illustrated schematic until then. Chosen pitch is locked to the booking
 * (backend pitch_locked) — never allocated to anyone else.
 */
const PitchMapPage = () => {
  const c = useContent();
  const mapCfg = c("pitchmap.map") || {};
  const photoUrl = mapCfg.photo_url || "";
  const pinPositions = useMemo(
    () => ({ ...DEFAULT_PITCH_POSITIONS, ...(mapCfg.positions || {}) }),
    [mapCfg.positions]
  );
  // Real drone-photo layer (hyper-real map) — admin can hide it anytime
  // (Content tab → "Aerial photo view" switch) without losing photo/pins.
  const aerialCfg = c("pitchmap.aerial") || {};
  const aerialUrl = aerialCfg.photo_url || "";
  const aerialEnabled = c("pitchmap.aerial_enabled") !== "0";
  // Official site plan layer — likewise hideable from the website (Content tab),
  // always stays available in the admin editor.
  const planEnabled = c("pitchmap.plan_enabled") !== "0";
  const aerialPins = useMemo(
    () => ({ ...AERIAL_PITCH_POSITIONS, ...(aerialCfg.positions || {}) }),
    [aerialCfg.positions]
  );
  // Admin-placed map features (trees, washroom, BBQ…) — shared by 3D + photo layers
  const mapFeatures = c("pitchmap.features");
  // Admin-drawn geometry: custom park boundary + gravel paths (3D model only)
  const mapGeometry = c("pitchmap.geometry");

  const [startDate, setStartDate] = useState(todayPlus(14));
  const [endDate, setEndDate] = useState(todayPlus(17));
  const [guests, setGuests] = useState(2);
  const [data, setData] = useState(null); // { pitches, lodges }
  const [loading, setLoading] = useState(false);
  const [lakeFocus, setLakeFocus] = useState(null); // "Daisy Lake" | "Walnut Lake" | null
  // If the admin hides a view while a guest is looking at it, fall back to 3D
  const [viewMode, setViewMode] = useState("3d"); // "3d" | "aerial" | "plan"
  useEffect(() => {
    if ((viewMode === "aerial" && !aerialEnabled) || (viewMode === "plan" && !planEnabled)) setViewMode("3d");
  }, [viewMode, aerialEnabled, planEnabled]);
  const webglOk = useMemo(() => {
    try {
      const c = document.createElement("canvas");
      return !!(c.getContext("webgl2") || c.getContext("webgl"));
    } catch {
      return false;
    }
  }, []);
  const [selected, setSelected] = useState(null); // unit id
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [previewFor, setPreviewFor] = useState(null); // touch-tap preview bubble (mobile has no hover)
  const [dismissedPreview, setDismissedPreview] = useState(null); // X-closed preview — stays hidden until the mouse leaves that pin
  const [galleryFor, setGalleryFor] = useState(null); // unit id with the big photo popup open
  const [galleryMode, setGalleryMode] = useState("photos"); // "photos" | "360"
  const [unitMedia, setUnitMedia] = useState({}); // id -> { images, panorama_url }

  const nights = useMemo(() => {
    const n = Math.round((new Date(endDate) - new Date(startDate)) / 86400000);
    return Number.isFinite(n) ? n : 0;
  }, [startDate, endDate]);

  // Per-unit photos/360 from the pitch media library
  useEffect(() => {
    axios.get(`${API}/units`).then(({ data: ud }) => {
      const map = {};
      (ud.types || []).forEach((t) =>
        (t.units || []).forEach((u) => {
          map[u.id] = { images: u.images || [], panorama_url: u.panorama_url || "" };
        })
      );
      setUnitMedia(map);
    }).catch(() => {});
  }, []);

  const load = async () => {
    if (!startDate || !endDate || nights < 1) return;
    setLoading(true);
    setSelected(null);
    setQuote(null);
    try {
      const { data: d } = await axios.get(`${API}/pitch-map/availability`, {
        params: { start_date: startDate, end_date: endDate },
      });
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const pitchById = useMemo(() => {
    const map = {};
    (data?.pitches || []).forEach((p) => (map[p.id] = p));
    (data?.lodges || []).forEach((l) => (map[l.id] = { ...l, sub_type: "lodge" }));
    return map;
  }, [data]);

  // Exact quote for the chosen unit
  useEffect(() => {
    if (!selected || !pitchById[selected]) {
      setQuote(null);
      return;
    }
    const meta = pitchById[selected];
    const isLodge = meta.sub_type === "lodge";
    setQuoteLoading(true);
    axios
      .post(`${API}/quote`, {
        unit_type: isLodge ? "lodge" : "touring",
        unit_id: selected,
        start_date: startDate,
        end_date: endDate,
        guests,
      })
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null))
      .finally(() => setQuoteLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, guests, startDate, endDate]);

  const sel = selected ? pitchById[selected] : null;
  const selMedia = selected ? unitMedia[selected] : null;

  // In-page booking popup — the guest never leaves the map. We mirror the
  // deep-link params into the URL so <Booking/> locks the pitch as usual,
  // and restore the clean map URL on close (same scroll spot, same pin state).
  const [bookingFor, setBookingFor] = useState(null);
  const openBooking = (unitId) => {
    window.history.pushState(null, "", `${window.location.pathname}?pitch=${encodeURIComponent(unitId)}&start=${startDate}&end=${endDate}&guests=${guests}#book`);
    setGalleryFor(null);
    setPreviewFor(null);
    setBookingFor(unitId);
  };
  const closeBooking = () => {
    window.history.pushState(null, "", window.location.pathname);
    setBookingFor(null);
  };

  // Lock background scroll while a popup (gallery or booking) is open
  useEffect(() => {
    document.body.style.overflow = galleryFor || bookingFor ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [galleryFor, bookingFor]);

  // Active flat-map layer: real aerial photo or the official site plan
  const activePhotoUrl = viewMode === "aerial" ? aerialUrl : photoUrl;
  const activePositions = viewMode === "aerial" ? aerialPins : pinPositions;
  const activeZones = viewMode === "aerial" ? AERIAL_LAKE_ZONES : LAKE_ZONES;
  const photoMode = viewMode !== "3d" && !!activePhotoUrl;

  const zoomStyle = lakeFocus
    ? { transform: "scale(1.6)", transformOrigin: activeZones[lakeFocus].origin, transition: "transform 0.5s ease" }
    : { transform: "scale(1)", transition: "transform 0.5s ease" };

  // Zoom to a lake (flat maps: zoom + scroll into view; 3D: camera flies there)
  const focusLake = (lake) => {
    const next = lakeFocus === lake ? null : lake;
    setLakeFocus(next);
    if (next && viewMode !== "3d") {
      setTimeout(() => {
        document
          .querySelector(`[data-testid="pitchmap-zone-${next.split(" ")[0].toLowerCase()}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
    }
  };

  const pinStateClass = (available, isSel) =>
    isSel
      ? "bg-[#B76E79] text-white ring-2 ring-white scale-125 z-20"
      : available
        ? "bg-white text-[#0F0E0E] ring-2 ring-[#3B8C4E] hover:scale-125 cursor-pointer z-10"
        : "bg-[#9a9187]/80 text-white/80 ring-1 ring-white/40 cursor-not-allowed opacity-70";

  const renderPhotoPin = (u, available) => {
    const pos = activePositions[u.id];
    if (!pos) return null;
    const isSel = selected === u.id;
    const isLodge = u.sub_type === "lodge";
    const hasPreview = !!unitMedia[u.id]?.images?.[0];
    const handlePinClick = (e) => {
      e.stopPropagation();
      const isTouch = typeof window !== "undefined" && !window.matchMedia("(hover: hover)").matches;
      if (isTouch && hasPreview) {
        // touch: first tap previews, second tap selects
        if (previewFor === u.id) {
          setPreviewFor(null);
          if (available) setSelected(isSel ? null : u.id);
        } else {
          setPreviewFor(u.id);
        }
        return;
      }
      if (available) setSelected(isSel ? null : u.id);
    };
    const label = isLodge ? lodgeShortName(u.name) : pitchNumber(u);
    // Aerial photo is landscape — pins sit closer together, so they render smaller.
    // Lodges get a wider name pill ("Alpine") instead of a numbered circle.
    const sizeCls = isLodge
      ? viewMode === "aerial"
        ? "-mt-2.5 -translate-x-1/2 h-5 px-2 text-[8px] whitespace-nowrap"
        : "-mt-3.5 -translate-x-1/2 h-7 px-2.5 text-[10px] whitespace-nowrap"
      : viewMode === "aerial"
        ? "-ml-2.5 -mt-2.5 w-5 h-5 text-[8px]"
        : "-ml-3.5 -mt-3.5 w-7 h-7 text-[11px]";
    return (
      <button
        key={u.id}
        type="button"
        onClick={handlePinClick}
        onMouseLeave={() => { if (dismissedPreview === u.id) setDismissedPreview(null); }}
        className={`absolute ${sizeCls} rounded-full font-bold shadow-lg flex items-center justify-center transition-all pin-hover ${previewFor === u.id ? "pin-preview-open" : ""} ${pinStateClass(available, isSel)}`}
        style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
        title={u.name}
        data-testid={`pitch-${u.id}`}
      >
        {label}
        {!galleryFor && dismissedPreview !== u.id && unitMedia[u.id]?.images?.[0] && (
          <span
            className={`pin-preview pointer-events-none absolute ${pos.x < 32 ? "left-0 -translate-x-2" : pos.x > 68 ? "left-auto right-0 translate-x-2" : "left-1/2 -translate-x-1/2"} ${pos.y < 30 ? "top-full mt-2" : "bottom-full mb-2"} z-40 w-64 sm:w-[26rem] rounded-xl overflow-hidden border border-white/60 shadow-2xl bg-[#0F0E0E]`}
            data-testid={`pin-preview-${u.id}`}
          >
            <span
              role="button"
              aria-label="Open photo gallery"
              onClick={(e) => { e.stopPropagation(); setPreviewFor(null); setGalleryFor(u.id); setGalleryMode("photos"); }}
              className="pointer-events-auto block cursor-zoom-in"
              data-testid={`pin-preview-photo-${u.id}`}
            >
              <img src={unitMedia[u.id].images[0]} alt="" className="w-full h-40 sm:h-60 object-cover block" draggable={false} />
            </span>
            <span
              role="button"
              aria-label="Close preview"
              onClick={(e) => { e.stopPropagation(); setPreviewFor(null); setDismissedPreview(u.id); }}
              className="pointer-events-auto absolute top-1.5 right-1.5 p-1 rounded-full bg-black/70 text-white cursor-pointer hover:bg-[#B76E79] shadow"
              data-testid={`pin-preview-close-${u.id}`}
            >
              <X size={12} />
            </span>
            {unitMedia[u.id]?.panorama_url && (
              <span
                role="button"
                aria-label="Open 360 degree view"
                onClick={(e) => { e.stopPropagation(); setPreviewFor(null); setGalleryFor(u.id); setGalleryMode("360"); }}
                className="pin-preview-360 pointer-events-auto absolute top-1.5 left-1.5 px-2 py-1 rounded-full bg-[#B76E79] text-white text-[9px] sm:text-[10px] font-bold tracking-[0.06em] cursor-pointer hover:bg-[#8C4E58] shadow"
                data-testid={`pin-preview-360-${u.id}`}
              >
                360°
              </span>
            )}
            <span className="block px-3 py-2 text-[10px] sm:text-xs uppercase tracking-[0.08em] text-[#F5EFE6] text-left leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
              {(isLodge ? u.name : shortLabel(u))}{u.nightly ? ` · £${u.nightly}/nt` : ""}
            </span>
          </span>
        )}
      </button>
    );
  };

  const renderSvgPitch = (group, p) => {
    const id = `${group.idPrefix}-${String(p.idn ?? p.n).padStart(2, "0")}`;
    const info = pitchById[id];
    const available = info ? info.available : false;
    const isSel = selected === id;
    const baseR = 11;
    return (
      <g
        key={id}
        onClick={() => available && setSelected(isSel ? null : id)}
        className={available ? "cursor-pointer" : "cursor-not-allowed"}
        data-testid={`pitch-${id}`}
        style={{ transition: "transform 0.15s" }}
      >
        <circle
          cx={p.x}
          cy={p.y}
          r={isSel ? baseR + 3 : baseR}
          fill={isSel ? "#B76E79" : available ? "#FFFFFF" : "#E3DACB"}
          stroke={isSel ? "#B76E79" : available ? group.color : "#C9BCA8"}
          strokeWidth={isSel ? 3 : 2}
          strokeDasharray={available || isSel ? "0" : "3 3"}
        />
        {!available && !isSel && (
          <line x1={p.x - 7} y1={p.y - 7} x2={p.x + 7} y2={p.y + 7} stroke="#C9BCA8" strokeWidth={2} />
        )}
        <text x={p.x} y={p.y + 3.5} textAnchor="middle" fontSize={10} fontWeight={700} fill={isSel ? "#FFFFFF" : available ? group.color : "#B5A99E"} fontFamily="Arial, sans-serif" pointerEvents="none">
          {p.n}
        </text>
      </g>
    );
  };

  const renderSvgLodge = (l) => {
    const info = pitchById[l.id];
    const available = info ? info.available : false;
    const isSel = selected === l.id;
    return (
      <g key={l.id} onClick={() => available && setSelected(isSel ? null : l.id)} className={available ? "cursor-pointer" : "cursor-not-allowed"} data-testid={`map-lodge-${l.id}`}>
        <rect x={l.x - 13} y={l.y - 13} width={26} height={26} rx={6} fill={isSel ? "#B76E79" : available ? MAP_COLORS.lodge : "#E3DACB"} stroke={isSel ? "#8C4E58" : "transparent"} strokeWidth={2} />
        <path d={`M${l.x - 6} ${l.y + 1} L${l.x} ${l.y - 5} L${l.x + 6} ${l.y + 1} M${l.x - 4} ${l.y + 1} L${l.x - 4} ${l.y + 6} L${l.x + 4} ${l.y + 6} L${l.x + 4} ${l.y + 1}`} stroke="#0F0E0E" strokeWidth={1.6} fill="none" strokeLinecap="round" strokeLinejoin="round" pointerEvents="none" />
        {!available && !isSel && <line x1={l.x - 9} y1={l.y - 9} x2={l.x + 9} y2={l.y + 9} stroke="#B5A99E" strokeWidth={2} />}
      </g>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5EFE6]" data-testid="pitchmap-page">
      <Seo
        title="Pick Your Pitch · Interactive Park Map"
        description="Choose your exact pitch or lodge at Walnut Lakes — explore the park in 3D or aerial view and book the precise spot you want at this adults-only Lincolnshire holiday park."
        path="/pick-your-pitch"
      />
      <header className="bg-[#0F0E0E] py-5 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-[#F5EFE6]/70 hover:text-[#E4B2A8] text-xs uppercase tracking-[0.2em] transition" data-testid="pitchmap-back">
            <ArrowLeft size={14} /> Back to site
          </Link>
          <img src={PARK.logo} alt="Walnut Lakes" className="h-14 w-auto logo-blend" />
          <span className="w-24" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <LuxeOverline>Pick your pitch</LuxeOverline>
        <h1 className="font-serif text-4xl md:text-5xl text-[#14100F] mt-4">Choose your exact spot on the map</h1>
        <p className="mt-3 text-[#6A5F58] max-w-2xl">
          Pick your dates, tap Daisy Lake or Walnut Lake, then choose the pitch you love — it's guaranteed yours,
          never re-allocated. The lodges are on the map too.
        </p>
        <div className="mt-4 flex items-center gap-3 max-w-2xl" data-testid="pitchmap-hint">
          <MapPin size={13} className="text-[#B76E79] shrink-0" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] leading-relaxed">
            Tap any free pitch — or a lodge — to see its details and book it
          </span>
          <span className="hidden sm:block flex-1 h-px bg-[#DDD1BE]" />
        </div>

        {/* Step 1 — dates */}
        <div className="mt-8 bg-white border border-[#DDD1BE] rounded-2xl p-5 flex flex-wrap items-end gap-4" data-testid="pitchmap-dates">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] font-semibold">Arrival</label>
            <input type="date" value={startDate} min={todayPlus(0)} onChange={(e) => setStartDate(e.target.value)} className="mt-1 block h-11 rounded-lg border border-[#DDD1BE] bg-[#F5EFE6] px-3 text-sm" data-testid="pitchmap-start" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] font-semibold">Departure</label>
            <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1 block h-11 rounded-lg border border-[#DDD1BE] bg-[#F5EFE6] px-3 text-sm" data-testid="pitchmap-end" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] font-semibold">Guests</label>
            <select value={guests} onChange={(e) => setGuests(parseInt(e.target.value, 10))} className="mt-1 block h-11 rounded-lg border border-[#DDD1BE] bg-[#F5EFE6] px-3 text-sm" data-testid="pitchmap-guests">
              {[1, 2, 3, 4].map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
          <div className="text-sm text-[#6A5F58] flex items-center gap-2 pb-1">
            {loading ? (
              <><Loader2 size={14} className="animate-spin" /> Checking every pitch…</>
            ) : data ? (
              <><CalendarDays size={14} className="text-[#B76E79]" /> {nights} night{nights !== 1 ? "s" : ""} · green rings are free</>
            ) : null}
          </div>
        </div>

        {/* Step 2 — the map, always stretched full width */}
        <div className="mt-4 relative">
          <div className="rounded-2xl border border-[#DDD1BE] bg-white p-4 md:p-6 shadow-sm overflow-hidden" data-testid="pitchmap-map">
            <div className="flex gap-2 mb-4 flex-wrap">
              {["Daisy Lake", "Walnut Lake"].map((lake) => (
                <button
                  key={lake}
                  type="button"
                  onClick={() => focusLake(lake)}
                  className={`px-4 py-2 rounded-full text-[11px] font-brand tracking-[0.14em] uppercase transition ${
                    lakeFocus === lake ? "bg-[#0F0E0E] text-[#F5EFE6]" : "bg-[#F5EFE6] text-[#0F0E0E] hover:bg-[#EDE3D2]"
                  }`}
                  data-testid={`pitchmap-lake-${lake.split(" ")[0].toLowerCase()}`}
                >
                  {lake}
                </button>
              ))}
              {lakeFocus && (
                <button type="button" onClick={() => setLakeFocus(null)} className="text-[10px] uppercase tracking-[0.14em] text-[#6A5F58] hover:text-[#B76E79] px-2" data-testid="pitchmap-zoom-out">
                  Show whole park
                </button>
              )}
              {webglOk && (
                <span className="ml-auto inline-flex rounded-full border border-[#DDD1BE] overflow-hidden" data-testid="pitchmap-view-toggle">
                  <button
                    type="button"
                    onClick={() => setViewMode("3d")}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-[10px] font-brand tracking-[0.12em] uppercase transition ${viewMode === "3d" ? "bg-[#0F0E0E] text-[#F5EFE6]" : "bg-white text-[#6A5F58] hover:bg-[#F5EFE6]"}`}
                    data-testid="pitchmap-view-3d"
                  >
                    <Boxes size={12} /> 3D view
                  </button>
                  {aerialUrl && aerialEnabled && (
                    <button
                      type="button"
                      onClick={() => setViewMode("aerial")}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-[10px] font-brand tracking-[0.12em] uppercase transition ${viewMode === "aerial" ? "bg-[#0F0E0E] text-[#F5EFE6]" : "bg-white text-[#6A5F58] hover:bg-[#F5EFE6]"}
                    `}
                      data-testid="pitchmap-view-aerial"
                    >
                      <MapPin size={12} /> Aerial
                    </button>
                  )}
                  {planEnabled && (
                    <button
                      type="button"
                      onClick={() => setViewMode("plan")}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-[10px] font-brand tracking-[0.12em] uppercase transition ${viewMode === "plan" ? "bg-[#0F0E0E] text-[#F5EFE6]" : "bg-white text-[#6A5F58] hover:bg-[#F5EFE6]"}`}
                      data-testid="pitchmap-view-plan"
                    >
                      <MapIcon size={12} /> Site plan
                    </button>
                  )}
                </span>
              )}
            </div>

            {viewMode === "3d" && webglOk ? (
              /* ---- Interactive 3D park model ---- */
              <React.Suspense
                fallback={
                  <div className="h-[560px] rounded-xl bg-[#EDF3E8] flex items-center justify-center text-sm text-[#6A5F58]">
                    <Loader2 size={15} className="animate-spin mr-2" /> Building the 3D park…
                  </div>
                }
              >
                <PitchMap3D
                  pitches={data?.pitches || []}
                  lodges={data?.lodges || []}
                  positions={pinPositions}
                  selected={selected}
                  onSelect={setSelected}
                  lakeFocus={lakeFocus}
                  features={mapFeatures}
                  geometry={mapGeometry}
                  media={unitMedia}
                  previewFor={previewFor}
                  onPreview={setPreviewFor}
                  onGallery={(id, m) => { setPreviewFor(null); setGalleryFor(id); setGalleryMode(m || "photos"); }}
                  suppressPreviews={!!galleryFor}
                  dismissedFor={dismissedPreview}
                  onPreviewDismiss={(id) => { setPreviewFor(null); setDismissedPreview(id); }}
                  onPreviewLeave={(id) => { if (dismissedPreview === id) setDismissedPreview(null); }}
                  onDismissPreview={() => setPreviewFor(null)}
                  labelOf={(u) =>
                    u.sub_type === "lodge"
                      ? lodgeShortName(u.name)
                      : pitchNumber(u)
                  }
                />
              </React.Suspense>
            ) : photoMode ? (
              /* ---- Photo layer with pins (real aerial or official plan) ---- */
              <div className="relative rounded-xl overflow-hidden" data-testid="pitchmap-photo-wrap" onClick={() => setPreviewFor(null)}>
                <div className="relative" style={zoomStyle}>
                  <img src={activePhotoUrl} alt={viewMode === "aerial" ? "Drone photo of Walnut Lakes" : "Official site plan of Walnut Lakes"} className="w-full h-auto block" draggable={false} />
                  {/* lake click zones (invisible) */}
                  {Object.entries(activeZones).map(([lake, z]) => (
                    <button
                      key={lake}
                      type="button"
                      aria-label={`Zoom to ${lake}`}
                      onClick={() => focusLake(lake)}
                      className="absolute bg-transparent"
                      style={{ left: `${z.left}%`, top: `${z.top}%`, width: `${z.width}%`, height: `${z.height}%` }}
                      data-testid={`pitchmap-zone-${lake.split(" ")[0].toLowerCase()}`}
                    />
                  ))}
                  {(data?.pitches || []).map((p) => renderPhotoPin(p, p.available))}
                  {(data?.lodges || []).map((l) => renderPhotoPin({ ...l, sub_type: "lodge" }, l.available))}
                  {/* admin-placed amenities (washroom, BBQ…) — not trees, they'd clutter the photo */}
                  {(mapFeatures || []).filter((f) => f.type && f.type !== "tree").map((f) => {
                    const Icon = FEATURE_ICONS[f.type] || Tag;
                    return (
                      <span
                        key={f.id}
                        className="absolute -translate-x-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white text-[8px] uppercase tracking-[0.1em] font-semibold shadow-md backdrop-blur-sm pointer-events-none"
                        style={{ left: `${f.x}%`, top: `${f.y}%`, background: `${FEATURE_CHIP_COLORS[f.type] || "#0F0E0E"}E6` }}
                        data-testid={`map-feature-${f.id}`}
                      >
                        <Icon size={9} /> {f.label || f.type}
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* ---- Illustrated fallback until the aerial is uploaded ---- */
              <svg viewBox="0 0 1000 660" className="w-full h-auto" data-testid="pitchmap-svg">
                <g
                  style={{
                    transform: lakeFocus ? "scale(1.45)" : "scale(1)",
                    transformOrigin: lakeFocus ? LAKE_ZONES[lakeFocus].origin : "center",
                    transition: "transform 0.5s ease",
                  }}
                >
                  <rect x={0} y={0} width={1000} height={660} rx={16} fill="#FBF7F1" />
                  <path
                    d="M118,250 C112,152 196,92 280,96 C372,100 428,168 424,248 C420,332 348,392 262,388 C178,384 124,332 118,250 Z"
                    fill={MAP_COLORS.water} stroke={lakeFocus === "Daisy Lake" ? "#B76E79" : MAP_COLORS.waterStroke} strokeWidth={lakeFocus === "Daisy Lake" ? 4 : 2.5}
                    className="cursor-pointer" onClick={() => focusLake("Daisy Lake")} data-testid="pitchmap-zone-daisy"
                  />
                  <text x={268} y={250} textAnchor="middle" fontSize={30} fontStyle="italic" fill="#5E8280" fontFamily="Georgia, serif" pointerEvents="none">Daisy Lake</text>
                  <path
                    d="M548,378 C544,290 628,232 722,236 C826,240 898,306 894,384 C890,464 808,518 714,514 C618,510 552,458 548,378 Z"
                    fill={MAP_COLORS.water} stroke={lakeFocus === "Walnut Lake" ? "#B76E79" : MAP_COLORS.waterStroke} strokeWidth={lakeFocus === "Walnut Lake" ? 4 : 2.5}
                    className="cursor-pointer" onClick={() => focusLake("Walnut Lake")} data-testid="pitchmap-zone-walnut"
                  />
                  <text x={718} y={382} textAnchor="middle" fontSize={30} fontStyle="italic" fill="#5E8280" fontFamily="Georgia, serif" pointerEvents="none">Walnut Lake</text>
                  {MAP_LODGES.map(renderSvgLodge)}
                  {MAP_PITCH_GROUPS.map((g) => g.points.map((p) => renderSvgPitch(g, p)))}
                </g>
              </svg>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[10px] uppercase tracking-[0.14em] text-[#3A322E]">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-white border-2 border-[#3B8C4E]" /> Free — tap to book</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#B76E79]" /> Your pick</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#9a9187]" /> Taken</span>
            </div>
          </div>

          {/* Step 3 — selection detail: floats over the map on desktop, stacks below it on mobile */}
          {sel && (
          <div className="relative mt-4 lg:mt-0 lg:absolute lg:right-6 lg:top-24 lg:w-[330px] lg:max-h-[calc(100%-8rem)] lg:overflow-y-auto lg:shadow-2xl z-20 bg-white border border-[#DDD1BE] rounded-2xl p-6" data-testid="pitchmap-detail">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="absolute top-3 left-3 z-20 p-1.5 rounded-full bg-[#0F0E0E]/60 text-white hover:bg-[#B76E79] transition"
                aria-label="Close pitch details"
                data-testid="pitchmap-detail-close"
              >
                <X size={14} />
              </button>
              <div>
                {selMedia?.images?.[0] ? (
                  <div className="relative -mx-2 -mt-2 mb-4">
                    <img src={selMedia.images[0]} alt={sel.name} className="w-full h-60 sm:h-72 object-cover rounded-xl" data-testid="pitchmap-detail-photo" />
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-[#0F0E0E]/75 via-[#0F0E0E]/10 to-transparent pointer-events-none" />
                    <div className="absolute bottom-3 left-4 right-4">
                      <p className="text-[9px] uppercase tracking-[0.22em] text-white/85 font-semibold">Your pick</p>
                      <h3 className="font-serif text-2xl text-white mt-1 leading-tight" data-testid="pitchmap-detail-name">{sel.name}</h3>
                    </div>
                    {selMedia.panorama_url && (
                      <button type="button" onClick={() => { setGalleryFor(selected); setGalleryMode("360"); }} className="absolute top-2.5 right-2.5 px-2.5 py-1 rounded-full bg-[#0F0E0E]/80 text-[#F5EFE6] text-[9px] uppercase tracking-[0.14em] font-semibold hover:bg-[#B76E79] transition" data-testid="pitchmap-detail-360">
                        360° view
                      </button>
                    )}
                  </div>
                ) : selMedia?.panorama_url ? (
                  <button type="button" onClick={() => { setGalleryFor(selected); setGalleryMode("360"); }} className="-mx-2 -mt-2 mb-4 w-[calc(100%+16px)] h-60 sm:h-72 rounded-xl bg-[#0F0E0E] flex flex-col items-center justify-center gap-2 text-[#F5EFE6] hover:bg-[#B76E79] transition" data-testid="pitchmap-detail-360">
                    <Compass size={22} className="text-[#E4B2A8]" />
                    <span className="text-[10px] uppercase tracking-[0.16em] font-semibold">Look around — 360° view</span>
                  </button>
                ) : null}
                {!selMedia?.images?.[0] && (
                  <>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">Your pick</p>
                    <h3 className="font-serif text-2xl text-[#14100F] mt-2" data-testid="pitchmap-detail-name">{sel.name}</h3>
                  </>
                )}
                <div className="mt-3 space-y-1.5 text-sm text-[#3A322E]">
                  {sel.sub_type !== "lodge" && (
                    <>
                      <p className="flex items-center gap-2"><MapPin size={13} className="text-[#B76E79]" /> {sel.lake}</p>
                      <p className="flex items-center gap-2"><Tent size={13} className="text-[#B76E79]" /> {sel.surface === "gravel" ? "Gravel hardstanding" : "Grass"} pitch</p>
                      <p className="flex items-center gap-2"><Zap size={13} className="text-[#B76E79]" /> {sel.electric ? "Electric hook-up included (15 kWh/day)" : "No electric — wild-camping vibe"}</p>
                    </>
                  )}
                  <p className="flex items-center gap-2"><Users size={13} className="text-[#B76E79]" /> {guests} guest{guests !== 1 ? "s" : ""} · {nights} night{nights !== 1 ? "s" : ""}</p>
                </div>
                <div className="mt-4 pt-4 border-t border-[#EDE3D2]">
                  {quoteLoading ? (
                    <p className="text-sm text-[#6A5F58] flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Pricing…</p>
                  ) : quote ? (
                    <>
                      <p className="text-sm text-[#6A5F58]">{gbp(quote.nightly)} a night</p>
                      <p className="font-serif text-3xl text-[#14100F] mt-1" data-testid="pitchmap-detail-total">{gbp(quote.total)}</p>
                      <p className="text-[11px] text-[#6A5F58] mt-1">
                        {quote.payment_type === "deposit" ? `${gbp(quote.payable_now)} deposit today · rest on arrival` : "Full payment today"}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-[#6A5F58]">{gbp(sel.nightly)} a night · exact total at checkout</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => openBooking(selected)}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[11px] font-brand tracking-[0.18em] uppercase"
                  data-testid="pitchmap-book-btn"
                >
                  <Check size={14} /> Book {sel.sub_type === "lodge" ? sel.name : shortLabel(sel)} — guaranteed yours
                </button>
                <p className="mt-3 text-[10px] text-[#6A5F58] text-center">Locked to your booking the moment you pay — never allocated to anyone else.</p>
              </div>
          </div>
          )}
        </div>

        {/* ---- Lake guides — illustrated pitch numbering per lake; tap to fly there ---- */}
        <div className="mt-10" data-testid="lake-guides">
          <div className="flex items-baseline gap-4 mb-4 flex-wrap">
            <h2 className="font-serif text-2xl text-[#14100F]">Know your lake</h2>
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#6A5F58]">Tap a card to see it on the map</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                lake: "Daisy Lake",
                img: lakeGuideDaisy,
                count: "Pitches 1–10",
                blurb: "Ten hardstanding pitches with electric hook-up wrapped around Daisy Lake's east and north banks — every one a waterside spot.",
              },
              {
                lake: "Walnut Lake",
                img: lakeGuideWalnut,
                count: "Pitches 1–5",
                blurb: "Five hardstanding pitches with electric hook-up in a row along Walnut Lake's bank, closest to the lodges.",
              },
            ].map((g) => (
              <button
                key={g.lake}
                type="button"
                data-testid={`lake-guide-${g.lake.split(" ")[0].toLowerCase()}`}
                onClick={() => {
                  document.querySelector('[data-testid="pitchmap-map"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                  if (lakeFocus !== g.lake) focusLake(g.lake);
                }}
                className="group text-left bg-white border border-[#DDD1BE] rounded-2xl overflow-hidden hover:border-[#B76E79] hover:shadow-xl transition"
              >
                <div className="overflow-hidden">
                  <img src={g.img} alt={`${g.lake} pitch layout guide`} className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-serif text-xl text-[#14100F]">{g.lake}</p>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-[#B76E79] font-semibold">{g.count}</span>
                  </div>
                  <p className="mt-1.5 text-sm text-[#6A5F58] leading-relaxed">{g.blurb}</p>
                  <p className="mt-2.5 text-[10px] uppercase tracking-[0.16em] text-[#0F0E0E] font-semibold group-hover:text-[#B76E79] transition">Show on the map →</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* pitch quick-list */}
        {data && (
          <div className="mt-6 bg-white border border-[#DDD1BE] rounded-2xl p-5" data-testid="pitchmap-list">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] font-semibold mb-3">
              {lakeFocus ? `${lakeFocus} pitches` : "All pitches"} — free for your dates
            </p>
            <div className="flex flex-wrap gap-2">
              {data.pitches
                .filter((p) => !lakeFocus || p.lake === lakeFocus)
                .map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    disabled={!p.available}
                    onClick={() => setSelected(selected === p.id ? null : p.id)}
                    className={`px-3 py-2 rounded-full text-[11px] transition border ${
                      selected === p.id
                        ? "bg-[#B76E79] text-white border-[#B76E79]"
                        : p.available
                          ? "bg-white text-[#0F0E0E] border-[#DDD1BE] hover:border-[#B76E79] hover:text-[#B76E79]"
                          : "bg-[#F5EFE6] text-[#B5A99E] border-[#EDE3D2] line-through cursor-not-allowed"
                    }`}
                    data-testid={`pitchlist-${p.id}`}
                  >
                    {shortLabel(p)}
                  </button>
                ))}
            </div>
          </div>
        )}
      </main>

      {galleryFor && pitchById[galleryFor] && (
        <PitchLightbox
          unit={pitchById[galleryFor]}
          media={unitMedia[galleryFor]}
          startDate={startDate}
          endDate={endDate}
          guests={guests}
          nights={nights}
          available={pitchById[galleryFor]?.available}
          initialMode={galleryMode}
          onClose={() => setGalleryFor(null)}
          onBook={openBooking}
        />
      )}

      {/* Mobile tap-preview — a page-level floating card (the in-pin cards are
          suppressed under 640px because map containers clip/trap them). Tap the
          photo for the full gallery, 360° for the tour, X to dismiss. */}
      {previewFor && pitchById[previewFor] && !galleryFor && !bookingFor && (
        <div
          className="sm:hidden fixed left-1/2 -translate-x-1/2 bottom-[12vh] z-[70] w-64 max-w-[calc(100vw-48px)] rounded-xl overflow-hidden border border-white/60 shadow-2xl bg-[#0F0E0E]"
          data-testid="pin-preview-mobile"
        >
          <button
            type="button"
            onClick={() => { setPreviewFor(null); setGalleryFor(previewFor); setGalleryMode("photos"); }}
            className="block w-full cursor-zoom-in"
            aria-label="Open photo gallery"
            data-testid="pin-preview-mobile-photo"
          >
            <img src={unitMedia[previewFor]?.images?.[0]} alt="" className="w-full h-40 object-cover block" draggable={false} />
          </button>
          <button
            type="button"
            onClick={() => setPreviewFor(null)}
            className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/70 text-white hover:bg-[#B76E79] shadow"
            aria-label="Close preview"
            data-testid="pin-preview-mobile-close"
          >
            <X size={12} />
          </button>
          {unitMedia[previewFor]?.panorama_url && (
            <button
              type="button"
              onClick={() => { setPreviewFor(null); setGalleryFor(previewFor); setGalleryMode("360"); }}
              className="absolute top-1.5 left-1.5 px-2 py-1 rounded-full bg-[#B76E79] text-white text-[9px] font-bold tracking-[0.06em] shadow"
              data-testid="pin-preview-mobile-360"
            >
              360°
            </button>
          )}
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.08em] text-[#F5EFE6] text-left leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
            {pitchById[previewFor].name}{pitchById[previewFor].nightly ? ` · £${pitchById[previewFor].nightly}/nt` : ""}
          </div>
        </div>
      )}

      {/* In-page booking popup — booking form floats over the map; closing it
          returns the guest to exactly where they were (same scroll, same pin) */}
      {bookingFor && (
        <div className="fixed inset-0 z-[110] overflow-y-auto bg-black/70 backdrop-blur-sm" data-testid="pitch-booking-modal">
          <div className="min-h-full px-3 py-6 sm:py-10 flex items-start justify-center">
            <div className="relative w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl bg-[#EAE0D0]">
              <button
                type="button"
                onClick={closeBooking}
                className="absolute top-3 right-3 z-30 p-2 rounded-full bg-[#0F0E0E]/70 text-white hover:bg-[#B76E79] transition"
                aria-label="Close booking"
                data-testid="pitch-booking-close"
              >
                <X size={16} />
              </button>
              <Booking />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PitchMapPage;
